import type { AppMessages } from '../../types';

export const app: AppMessages = {
  common: {
    loading: 'Wird geladen...',
    saving: 'Wird gespeichert...',
    save: 'Speichern',
    back: 'Zurück',
    error: 'Fehler',
    notSet: 'Nicht festgelegt',
    user: 'Benutzer',
    system: 'System',
    copied: 'Kopiert!',
    copy: 'Kopieren',
    cancel: 'Abbrechen',
    creating: 'Wird erstellt...',
    processing: 'Wird verarbeitet...',
    you: 'Sie',
    stars: 'Stars',
    seeAll: 'Alle anzeigen',
    filter: 'Filtern',
    amount: 'Betrag',
    date: 'Datum',
    details: 'Details',
    mustBeLoggedIn: 'Sie müssen angemeldet sein',
    daysShort: 'T',
    hoursShort: 'Std',
    minutesShort: 'Min',
    justNow: 'Gerade eben',
    noTitle: 'Kein Titel',
    payment: 'Zahlung',
    payments: 'Zahlungen',
  },
  layout: {
    dashboard: 'Dashboard',
    invoices: 'Rechnungen',
    ledger: 'Hauptbuch',
    withdraws: 'Auszahlungen',
    redirects: 'Weiterleitungen',
    settings: 'Einstellungen',
    admin: 'Admin',
    profile: 'Profil',
    logout: 'Abmelden',
    tonConfigured: 'TON konfiguriert',
    configureTon: 'TON konfigurieren',
    tonNotConfigured: 'TON-Adresse nicht konfiguriert',
    active: 'Aktiv',
    walletConnect: 'Wallet verbinden',
    walletTitle: 'Verbinde deine TON-Wallet',
    walletSubtitle: 'Gib die TON-Adresse ein, die deine Auszahlungen erhalten soll.',
    walletConnectedTitle: 'Wallet verbunden',
    walletConnectedSubtitle: 'Deine Auszahlungen werden an diese Adresse gesendet.',
    walletCurrentAddress: 'Verbundene Adresse',
    walletAddressLabel: 'TON-Adresse',
    walletHint: 'User-friendly-Format (EQ… / UQ…) oder Raw-Format (0:…) akzeptiert.',
    walletInvalidAddress: 'Ungültige TON-Adresse',
    walletUpdate: 'Adresse aktualisieren',
    walletChangeAddress: 'Adresse ändern',
    walletDisconnect: 'Trennen',
    walletDisconnectConfirmTitle: 'Wallet trennen?',
    walletDisconnectConfirmBody: 'Du kannst keine Auszahlungen anfordern, bis du eine neue Adresse verbindest.',
    walletSaved: 'Wallet erfolgreich verbunden',
    walletDisconnected: 'Wallet getrennt',
    newPaymentLink: 'Neuer Zahlungslink',
    openBot: 'Telegram-Bot',
    helpCenter: 'Hilfecenter',
  },
  login: {
    seoTitle: 'Anmelden | Telegram Stars Zahlungsplattform',
    seoDescription: 'Melden Sie sich bei Ihrem Telegram Stars Payment Platform Konto an',
    signIn: 'Anmelden',
    signUp: 'Registrieren',
    email: 'E-Mail',
    password: 'Passwort',
    displayNameOptional: 'Anzeigename (optional)',
    displayNamePlaceholder: 'Ihr Name',
    emailPlaceholder: 'ihre@email.com',
    signInButton: 'Anmelden',
    signUpButton: 'Registrieren',
    signInWithGoogle: 'Mit Google anmelden',
    or: 'ODER',
    welcome: 'Willkommen',
    subtitle: 'Melden Sie sich an oder erstellen Sie ein Konto, um zu beginnen',
    minPasswordHint: 'Mindestens 6 Zeichen',
    tipTelegram: '💡 Tipp: Sie können Ihr Telegram-Konto nach der Registrierung in den Einstellungen verknüpfen',
    continueWithTelegram: 'Mit Telegram fortfahren',
    telegramAuthError: 'Telegram-Anmeldung fehlgeschlagen. Bitte erneut versuchen.',
    featureInstant: 'Sofortige Kontoerstellung',
    featureManage: 'Verwalten Sie Ihre Telegram Stars Zahlungen',
    featureWithdraw: 'Zahlen Sie Ihre Mittel in TON aus',
    emailRequired: 'Bitte geben Sie Ihre E-Mail ein',
    emailInvalid: 'Ungültiges E-Mail-Format',
    passwordRequired: 'Bitte geben Sie Ihr Passwort ein',
    passwordRequiredSignup: 'Bitte geben Sie ein Passwort ein',
    passwordMinLength: 'Das Passwort muss mindestens 6 Zeichen enthalten',
    genericError: 'Ein Fehler ist aufgetreten',
    recaptchaFailed: 'reCAPTCHA-Überprüfung fehlgeschlagen. Bitte versuchen Sie es erneut.',
    invalidCredentials: 'Falsche E-Mail oder falsches Passwort. Wenn Sie noch kein Konto haben, erstellen Sie eines im Tab „Registrieren“.',
    wrongPassword: 'Falsches Passwort. Bitte überprüfen Sie Ihr Passwort.',
    tooManyAttempts: 'Zu viele Versuche. Bitte versuchen Sie es später erneut.',
    accountDisabled: 'Dieses Konto wurde deaktiviert. Kontaktieren Sie den Support.',
    authNotEnabled: 'E-Mail/Passwort-Authentifizierung ist nicht aktiviert. Überprüfen Sie die Firebase-Konfiguration.',
    emailInUse: 'Diese E-Mail wird bereits verwendet. Melden Sie sich mit dieser E-Mail an.',
    googleAuthError: 'Bei der Google-Authentifizierung ist ein Fehler aufgetreten',
    popupClosed: 'Das Popup wurde geschlossen. Bitte versuchen Sie es erneut.',
    popupBlocked: 'Das Popup wurde blockiert. Bitte erlauben Sie Popups und versuchen Sie es erneut.',
    popupCancelled: 'Es ist nur eine Popup-Anfrage gleichzeitig erlaubt.',
  },
  dashboard: {
    loading: 'Dashboard wird geladen...',
    overallSummary: 'Gesamtübersicht',
    lastMonth: 'Letzter Monat',
    lastWeek: 'Letzte Woche',
    lastYear: 'Letztes Jahr',
    balance: 'Saldo',
    available: 'Verfügbar',
    totalEarned: 'Gesamt verdient',
    fromLastMonth: 'im Vergleich zum letzten Monat',
    recentPayments: 'Letzte Zahlungen',
    last7days: 'Letzte 7 Tage',
    last30days: 'Letzte 30 Tage',
    quickActions: 'Schnellaktionen',
    createLink: 'Link erstellen',
    createLinkDesc: 'Neuen Zahlungslink generieren',
    withdraw: 'Auszahlen',
    withdrawDesc: 'Stars in TON auszahlen',
    invoices: 'Rechnungen',
    invoicesDesc: 'Zahlungshistorie anzeigen',
    ledger: 'Hauptbuch',
    ledgerDesc: 'Alle Transaktionen anzeigen',
    recentActivity: 'Letzte Aktivität',
    noRecentActivity: 'Keine aktuelle Aktivität',
    mostRecent: 'Neueste',
    noRecentInvoices: 'Keine aktuellen Rechnungen',
    allInvoices: 'Alle Rechnungen',
    paymentReceived: 'Zahlung erhalten: {title}',
    withdrawalRequested: 'Auszahlung angefordert: {amount} {suffix}',
    transaction: 'Transaktion: {amount} {suffix}',
    invoiceLabel: 'Rechnung #{id}',
    failedLoadBalance: 'Saldo konnte nicht geladen werden',
    chartAmount: 'Betrag',
  },
  invoices: {
    loading: 'Wird geladen...',
    allInvoices: 'Alle Rechnungen',
    paid: 'Bezahlt',
    sent: 'Gesendet',
    created: 'Erstellt',
    expired: 'Abgelaufen',
    filter: 'Filtern',
    createInvoice: '+ Rechnung erstellen',
    noInvoices: 'Keine Rechnungen gefunden',
    relatedTo: 'Bezogen auf:',
    paymentMode: '{mode}-Zahlung',
    copyLink: 'Link kopieren',
    copyLinkTitle: 'Rechnungslink kopieren',
    linkNotAvailable: 'Rechnungslink nicht verfügbar',
    linkCopied: 'Rechnungslink in die Zwischenablage kopiert!',
    copyFailed: 'Link konnte nicht kopiert werden',
    failedLoad: 'Rechnungen konnten nicht geladen werden',
    statusPaid: 'bezahlt',
    statusSent: 'gesendet',
    statusCreated: 'erstellt',
    statusExpired: 'abgelaufen',
    typeSingleUse: 'Einmalnutzung',
    typeRecurring: 'wiederkehrend',
  },
  createInvoice: {
    back: 'Zurück',
    title: 'Zahlungslink erstellen',
    subtitle: 'Generieren Sie in Sekunden einen neuen Zahlungslink',
    successTitle: 'Zahlungslink erstellt!',
    successSubtitle: 'Ihr Zahlungslink ist bereit zum Teilen',
    paymentLink: 'Zahlungslink:',
    integrationTitle: 'Diesen Link auf Ihrer Website einbinden',
    integrationDesc: 'Unser Telegram-Bot führt Sie Schritt für Schritt (API, HTML-Button, Weiterleitungen).',
    openTutorial: 'Tutorial öffnen',
    testLink: 'Link testen',
    viewInvoices: 'Meine Rechnungen anzeigen',
    createNew: 'Neuen Link erstellen',
    shareTelegram: 'Auf Telegram teilen',
    paymentMode: 'Zahlungsmodus',
    modeStars: 'Stars',
    modeEur: 'EUR (€)',
    modeUsd: 'USD ($)',
    amountInteger: 'Betrag (ganze Zahl)',
    amountDecimal: 'Betrag (Dezimal)',
    amountRequired: 'Bitte geben Sie einen Betrag ein',
    amountIntegerError: 'Der Betrag muss eine ganze Zahl größer als 0 sein',
    amountNoDecimals: 'Stars-Betrag muss eine ganze Zahl sein (keine Dezimalstellen)',
    amountMustBeInteger: 'Stars-Betrag muss eine ganze Zahl sein',
    amountGreaterThanZero: 'Der Betrag muss größer als 0 sein',
    titleOptional: 'Titel (optional)',
    titlePlaceholder: 'Zahlungstitel (z. B. Zahlung für Dienstleistungen)',
    descriptionOptional: 'Beschreibung (optional)',
    descriptionPlaceholder: 'Detaillierte Zahlungsbeschreibung...',
    singleUse: 'Einmal-Link',
    singleUseTooltip: 'Wenn aktiviert, kann dieser Link nur einmal verwendet werden. Nach der Zahlung ist er nicht mehr gültig.',
    createLink: 'Link erstellen',
    cancel: 'Abbrechen',
    invalidAmount: 'Ungültiger Betrag',
    starsMustBeInteger: 'Der Betrag muss für Stars eine ganze Zahl sein',
    createError: 'Fehler beim Erstellen der Rechnung',
  },
  ledger: {
    title: 'Hauptbuch',
    typeColumn: 'Typ',
    description: 'Vollständiger Verlauf aller Ihrer Transaktionen',
    loading: 'Wird geladen...',
    noEntries: 'Derzeit keine Transaktionen.',
    typePayment: 'Zahlung',
    typeFee: 'Gebühr',
    typeNetPayment: 'Nettozahlung',
    typeWithdrawal: 'Auszahlung',
    typeAdjustment: 'Anpassung',
    amountStars: 'Betrag (Stars)',
    invoiceDetail: 'Rechnung: {title} ({amount} Stars)',
    paidBy: 'Bezahlt von: {payer}',
    payerId: '(ID: {id})',
    withdrawDetail: 'Auszahlung: {amount} Stars (Status: {status})',
    failedLoad: 'Hauptbuch konnte nicht geladen werden',
  },
  withdraws: {
    title: 'Meine Auszahlungsanfragen',
    newRequest: '+ Neue Anfrage',
    totalLedger: 'Hauptbuch gesamt',
    available: 'Verfügbar',
    pendingWithdrawals: 'Ausstehende Auszahlungen',
    hold21days: '21-Tage-Sperre',
    payoutNote: 'Auszahlungen werden manuell von einem Admin bearbeitet. Bei der Auszahlung fällt eine Gebühr von 3% an.',
    forecastTitle: 'Auszahlungsprognose',
    loadingForecast: 'Prognose wird geladen...',
    noForecast: 'Keine bevorstehenden Auszahlungstermine',
    paymentsCount: '({count} Zahlungen)',
    noRequests: 'Derzeit keine Auszahlungsanfragen.',
    createFirst: 'Erstellen Sie Ihre erste Anfrage',
    amount: 'Betrag:',
    destination: 'Ziel:',
    processedOn: 'Bearbeitet am:',
    externalRef: 'Externe Referenz:',
    errorLabel: 'Fehler:',
    rejectionReason: 'Ablehnungsgrund:',
    failedLoad: 'Auszahlungen konnten nicht geladen werden',
    loading: 'Wird geladen...',
  },
  createWithdraw: {
    title: 'Auszahlung anfordern',
    balanceSource: 'Saldo (Quelle: Backend-Hauptbuch)',
    available: 'Verfügbar: {amount} Stars',
    pending: 'Ausstehende Auszahlungen: {amount} Stars',
    waiting: '21-Tage-Sperre: {amount} Stars',
    minAndFee: 'Mindestauszahlung: {min} Stars · Plattformgebühr: 3% bei Auszahlung (manuelle Bearbeitung)',
    amountLabel: 'Betrag (Stars)',
    minPlaceholder: 'Minimum: {min}',
    feeHint: 'Brutto: {gross} Stars · Gebühr (3%): {fee} Stars · Sie erhalten: {net} Stars',
    useRegistered: 'Meine registrierte TON-Adresse verwenden',
    registeredAddress: 'Registrierte Adresse:',
    tonDestination: 'TON-Zieladresse',
    tonPlaceholder: 'EQAbc123...',
    tonFormatHint: 'Format: EQ... oder 0:... (Base64 oder Hex)',
    createRequest: 'Anfrage erstellen',
    cancel: 'Abbrechen',
    creating: 'Wird erstellt...',
    insufficientBalance: '⚠️ Unzureichender Saldo für Auszahlung (Minimum: {min} Stars)',
    amountMustBeInteger: 'Der Betrag muss eine positive ganze Zahl sein',
    createError: 'Fehler beim Erstellen der Auszahlungsanfrage',
  },
  redirects: {
    title: 'Weiterleitungslinks',
    description: 'Erstellen Sie Kurzlinks, die zu einer URL Ihrer Wahl weiterleiten. Das Teilen des Links /r/ihr-slug leitet Besucher zur konfigurierten URL.',
    newRedirect: 'Neue Weiterleitung',
    clicks: 'Klicks',
    unique: 'eindeutig',
    links: 'Links',
    modalTitle: 'Neue Weiterleitung',
    slugLabel: 'Slug (Link-Kennung)',
    slugRequired: 'Pflichtfeld',
    slugPattern: 'Nur Buchstaben, Zahlen, Bindestriche und Unterstriche',
    slugMax: 'Max. 64 Zeichen',
    slugReserved: 'Dieser Slug ist reserviert. Wählen Sie einen anderen.',
    slugPlaceholder: 'z. B.: meine-seite',
    urlLabel: 'Ziel-URL',
    urlInvalid: 'Ungültige URL',
    urlHttpOnly: 'Nur http- und https-URLs werden akzeptiert.',
    urlTooLong: 'URL zu lang (max. 2048 Zeichen).',
    urlPlaceholder: 'https://example.com/page',
    createButton: 'Weiterleitung erstellen',
    loading: 'Wird geladen…',
    empty: 'Keine Weiterleitungen. Klicken Sie auf „Neue Weiterleitung“, um eine zu erstellen.',
    copyLink: 'Link kopieren',
    linkCopied: 'Link kopiert',
    deleteTitle: 'Diese Weiterleitung löschen?',
    yes: 'Ja',
    no: 'Nein',
    delete: 'Löschen',
    totalClicks: 'Klicks gesamt',
    uniqueClicks: 'Eindeutige Klicks',
    avgClicks: 'Durchschn. Klicks/Besucher',
    loadError: 'Fehler beim Laden der Weiterleitungen',
    createSuccess: 'Weiterleitungslink erstellt',
    createError: 'Fehler beim Erstellen der Weiterleitung',
    deleteSuccess: 'Weiterleitung gelöscht',
    deleteError: 'Fehler beim Löschen der Weiterleitung',
  },
  adminWithdraws: {
    title: 'Administration - Auszahlungsanfragen',
    filterByStatus: 'Nach Status filtern:',
    all: 'Alle',
    noRequests: 'Keine Anfragen für diesen Status.',
    agent: 'Agent:',
    amount: 'Betrag:',
    destination: 'Ziel:',
    processedOn: 'Bearbeitet am:',
    externalRef: 'Ext. Ref.:',
    errorLabel: 'Fehler:',
    reason: 'Grund:',
    approve: '✓ Genehmigen',
    reject: '✗ Ablehnen',
    markPaid: '✓ Als bezahlt markieren',
    markFailed: '✗ Als fehlgeschlagen markieren',
    approveConfirm: 'Diese Auszahlungsanfrage genehmigen?',
    rejectPrompt: 'Ablehnungsgrund:',
    markPaidPrompt: 'Externe Auszahlungsreferenz (erforderlich — TON-Tx-Hash, Bankref., etc.):',
    markPaidRequired: 'externalRef ist erforderlich, um eine manuelle Auszahlung zu bestätigen.',
    markFailedPrompt: 'Fehlermeldung:',
    approveError: 'Fehler bei der Genehmigung der Auszahlung',
    rejectError: 'Fehler bei der Ablehnung der Auszahlung',
    updateError: 'Fehler beim Aktualisieren',
    failedLoad: 'Auszahlungen konnten nicht geladen werden',
    notAuthenticated: 'Nicht authentifiziert',
    operationFailed: 'Vorgang fehlgeschlagen',
    loading: 'Wird geladen...',
  },
  redirectResolve: {
    missingSlug: 'Slug fehlt',
    invalidLink: 'Ungültiger Link',
    notFound: 'Link nicht gefunden',
    notFoundTitle: 'Link nicht gefunden',
    redirecting: 'Weiterleitung...',
    redirectTitle: 'Weiterleitung',
  },
  errorBoundary: {
    title: 'Ein Fehler ist aufgetreten',
    subtitle: 'Entschuldigung, etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.',
    goHome: 'Zurück zur Startseite',
    reload: 'Seite neu laden',
    devDetails: 'Fehlerdetails (nur Dev):',
  },
};

export const settingsApp = {
  eyebrow: 'EINSTELLUNGEN',
  profile: 'Profil',
  email: 'E-Mail',
  telegramId: 'Telegram-ID',
  notLinked: 'Nicht verknüpft',
  linking: 'Wird verknüpft...',
  linkTelegram: 'Mein Telegram-Konto verknüpfen',
  openFromTelegram: '⚠️ Öffnen Sie diese Seite in Telegram, um Ihr Konto zu verknüpfen',
  displayName: 'Anzeigename',
  displayNameHint: 'Der Anzeigename wird bei der Kontoerstellung festgelegt',
  withdrawal: 'Auszahlung',
  tonAddress: 'TON-Zieladresse',
  tonAddressPlaceholder: 'EQAbc123... oder 0:...',
  tonAddressHint: 'Diese Adresse wird standardmäßig für Ihre Auszahlungen verwendet',
  tonUpdated: 'TON-Adresse erfolgreich aktualisiert',
  about: 'Über',
  commission: 'Plattformgebühr:',
  commissionValue: '3% bei jeder Zahlung',
  minWithdrawal: 'Mindestauszahlung:',
  minWithdrawalValue: '100 Stars',
  starsCurrency: 'Stars-Währung:',
  starsCurrencyValue: 'XTR',
  telegramWebAppNotAvailable: 'Telegram WebApp nicht verfügbar. Öffnen Sie diese Seite in Telegram.',
  telegramUserDataNotAvailable: 'Telegram-Benutzerdaten nicht verfügbar.',
  telegramLinked: 'Telegram-Konto erfolgreich verknüpft!',
  linkTelegramError: 'Fehler beim Verknüpfen des Telegram-Kontos',
  updateError: 'Fehler beim Aktualisieren',
  mustBeLoggedIn: 'Sie müssen angemeldet sein',
};
